import React, { useEffect, useRef, useState } from 'react'
import { Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/src/renderer/components/ui/Button'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { onAppEvent } from '@/src/renderer/lib/events'
import './FavoriteToggle.scss'

export interface FavoriteToggleProps {
  /** Ist gerade ein **Favorit** — der Stern ist dann gefüllt und der Knopf eingerastet. */
  favorite: boolean
  onFavoriteChange: (favorite: boolean) => void
  /**
   * Wofür der Stern steht: die Projekt-ID oder die IDs der gewählten Aufgaben. Schaltet ⇧⌘F genau
   * diese um, bewegt er sich wie beim Klick. Ohne die IDs bleibt er bei ⇧⌘F still.
   */
  subjectIds?: readonly string[]
  className?: string
}

type Motion = 'hop' | 'shake'

// Später als sonst (700ms): Wer auf den Stern zielt, will meist klicken, nicht lesen (Nutzer-Vorgabe, 2026-09-30).
const TOOLTIP_DELAY_MS = 1000

/**
 * **Der Stern** (#210, CONTEXT.md → Favorit): im Projekt-Kopf ganz links vor Liste | Stapel und in
 * der Kopfleiste der Aufgaben-Details. Der `Button` in `tertiary` — ohne Fläche, Rahmen und Schatten, damit er
 * neben den Knopfgruppen nicht als weitere Gruppe liest. `aria-pressed` sagt den Zustand, der Name,
 * was der nächste Klick tut. Tooltip mit ⇧⌘F.
 *
 * Per Klick gesetzt, **hüpft** der Stern und kommt gelb wieder an; per Klick entfernt, **schüttelt**
 * er den Kopf und wird grau (ausgesucht im Prototyp `tools/favorites-prototype/star-animations.html`).
 * Beim Klick und bei ⇧⌘F auf dasselbe (`subjectIds`); ein anderer Wechsel von außen (andere Aufgabe
 * gewählt, Menü) setzt den Stern still.
 *
 * @example
 * <FavoriteToggle favorite={project.favoriteOrder != null} onFavoriteChange={setFavorite} />
 */
export const FavoriteToggle: React.FC<FavoriteToggleProps> = ({ favorite, onFavoriteChange, subjectIds, className }) => {
  const { t } = useTranslation()
  const [motion, setMotion] = useState<Motion | null>(null)
  // Als `key`: Ein Klick mitten in der Bewegung startet die neue von vorn.
  const [run, setRun] = useState(0)
  // Während der Bewegung gilt schon der neue Zustand, auch wenn die Datenbank ihn noch nicht zurückgemeldet hat.
  const shown = motion === 'hop' ? true : motion === 'shake' ? false : favorite
  const play = (next: boolean) => {
    setMotion(next ? 'hop' : 'shake')
    setRun(n => n + 1)
  }

  // Die IDs kommen meist als frisches Array; über die Ref bleibt das Abo stehen.
  const subjectIdsRef = useRef(subjectIds)
  subjectIdsRef.current = subjectIds
  const motionRef = useRef<HTMLSpanElement>(null)
  useEffect(() => onAppEvent('favorite-shortcut', ({ ids, favorite: next }) => {
    const subject = subjectIdsRef.current
    if (!subject?.length || !subject.every(id => ids.includes(id))) return
    // Ein Tab hinter `display: none` hat keine Box: Dort liefe keine CSS-Animation, `animationend`
    // käme nie, und die Bewegung spielte erst beim nächsten Wechsel dorthin ab.
    if (!motionRef.current || motionRef.current.getClientRects().length === 0) return
    play(next)
  }), [])
  const label = t(shown ? 'favorites.remove' : 'favorites.add')
  return (
    <WithTooltip content={label} shortcut={{ kind: 'character', text: 'F', mod: true, shift: true }} delayDuration={TOOLTIP_DELAY_MS}>
      <Button
        variant="tertiary"
        className={['favorite-toggle', shown && 'favorite-toggle--on', className].filter(Boolean).join(' ')}
        aria-pressed={shown}
        aria-label={label}
        icon={(
          <span
            ref={motionRef}
            key={run}
            className={motion ? `favorite-toggle__motion favorite-toggle__motion--${motion}` : 'favorite-toggle__motion'}
            aria-hidden="true"
            // Die Sterne darin blenden mit eigenen Animationen über; deren Ende steigt hier auf.
            onAnimationEnd={e => { if (e.target === e.currentTarget) setMotion(null) }}
          >
            <Star className="favorite-toggle__outline" size={16} fill="none" />
            <Star className="favorite-toggle__fill" size={16} fill="currentColor" />
          </span>
        )}
        onClick={() => {
          play(!shown)
          onFavoriteChange(!shown)
        }}
      />
    </WithTooltip>
  )
}
